import { createSlice, createEntityAdapter } from "@reduxjs/toolkit";
import { notifyError, notifySuccess } from "./notification.slice";
import { authFailCheck } from "./auth.slice";
import CarService from "./../services/CarService";
import { createSelector } from "reselect";
import { instructorsSelectors } from "./instructors.slice";

const carsAdapter = createEntityAdapter({
  selectId: (car) => car.id,
  sortComparer: (a, b) =>
    a.transmission === b.transmission ? 0 : a.transmission ? 1 : -1,
});
const initialState = carsAdapter.getInitialState({
  isLoading: false,
  unassignedContractIds: [],
  contractIdsByCars: {},
  contracts: {},
  filter: {
    transmission: 0,
    status: 0,
    keyword: "",
    sortBy: null,
    sortOrder: 1,
    pageNumber: 1,
    pageSize: 25,
  },
});

const carsSlice = createSlice({
  name: "cars",
  initialState: initialState,
  reducers: {
    fetchRequest(state, action) {
      state.isLoading = true;
      state.filter = { ...state.filter, ...action.payload };
    },
    fetchFail(state, action) {
      state.isLoading = false;
    },
    fetchCarsSuccess(state, action) {
      const { payload } = action;
      state.isLoading = false;

      state.ids = [];
      state.entities = [];

      payload.forEach((item, index) => {
        state.ids[index] = item.id;
        state.entities[item.id] = item;
      });
    },
    updateCarSuccess(state, action) {
      state.isLoading = false;
      carsAdapter.upsertOne(state, action.payload);
    },
    createCarSuccess(state, action) {
      state.isLoading = false;
      carsAdapter.addOne(state, action.payload);
    },
    deleteCarSuccess(state, action) {
      state.isLoading = false;
      carsAdapter.removeOne(state, action.payload);
    },
    fetchContractsSuccess(state, action) {
      state.isLoading = false;
      state.unassignedContractIds = [];
      state.contractIdsByCars = {};
      action.payload.forEach((contract) => {
        if (contract.carId) {
          if (!state.contractIdsByCars[contract.carId]) {
            state.contractIdsByCars[contract.carId] = { contractIds: [] };
          }
          state.contractIdsByCars[contract.carId].contractIds.push(contract.id);
        } else {
          state.unassignedContractIds.push(contract.id);
        }
        state.contracts[contract.id] = contract;
      });
    },
    assignCarSuccess(state, action) {
      const { contractId, oldCarId, carId, assignedDate } = action.payload;
      state.isLoading = false;
      if (oldCarId) {
        state.contractIdsByCars[oldCarId].contractIds = state.contractIdsByCars[
          oldCarId
        ].contractIds.filter((id) => id !== contractId);
      } else {
        state.unassignedContractIds = state.unassignedContractIds.filter(
          (id) => id !== contractId
        );
      }
      if (!state.contractIdsByCars[carId]) {
        state.contractIdsByCars[carId] = { contractIds: [] };
      }
      state.contractIdsByCars[carId].contractIds.push(contractId);
      state.contracts[contractId].carAssignedDate = assignedDate;
    },
    unassignCarSuccess(state, action) {
      const { contractId, carId } = action.payload;
      state.isLoading = false;
      if (carId) {
        state.contractIdsByCars[carId].contractIds = state.contractIdsByCars[
          carId
        ].contractIds.filter((id) => id !== contractId);
        state.unassignedContractIds.push(contractId);
      }
      state.contracts[contractId].carId = 0;
      state.contracts[contractId].carAssignedDate = null;
    },
    createDrivingNoteSuccess(state, action) {
      const { payload } = action;
      state.isLoading = false;
      if (state.contracts[payload.contractId]) {
        if (!state.contracts[payload.contractId].drivingNotes) {
          state.contracts[payload.contractId].drivingNotes = [];
        }
        state.contracts[payload.contractId].drivingNotes.push(payload);
      }
    },
  },
});

export const {
  fetchRequest,
  fetchFail,
  fetchCarsSuccess,
  updateCarSuccess,
  createCarSuccess,
  deleteCarSuccess,
  fetchContractsSuccess,
  assignCarSuccess,
  unassignCarSuccess,
  createDrivingNoteSuccess,
} = carsSlice.actions;

export const carSelectors = carsAdapter.getSelectors((state) => state.cars);
const getUnassignedContractIds = (state) => state.cars.unassignedContractIds;
const getAllContracts = (state) => state.cars.contracts;
const getrContractIdsByCarMapFromProps = (state, props) =>
  state.cars.contractIdsByCars[props.carId];

export const selectUnassignedContracts = createSelector(
  [getUnassignedContractIds, getAllContracts],
  (ids, contracts) => ids.map((id) => contracts[id])
);

export const makeContractsByCarSelector = () => {
  return createSelector(
    [getAllContracts, getrContractIdsByCarMapFromProps],
    (contracts, car) => {
      if (!car) return [];
      return car.contractIds.map((id) => contracts[id]);
    }
  );
};

export default carsSlice.reducer;

export const fetchCars = (filter) => async (dispatch) => {
  dispatch(fetchRequest(filter));
  const response = await CarService.get(filter);
  if (response.ok) {
    dispatch(fetchCarsSuccess(response.data));
  } else {
    dispatch(authFailCheck(response));
    dispatch(fetchFail(response.data));
  }
};

export const updateCar = (carId, car) => async (dispatch, getState) => {
  dispatch(fetchRequest());

  const response = await CarService.update(carId, car);

  if (response.ok) {
    dispatch(notifySuccess(`${car.number} o'zgartirildi.`));
    const instructor = instructorsSelectors.selectById(
      getState(),
      car.instructorId
    );
    dispatch(
      updateCarSuccess({ id: carId, instructor: instructor.fullName, ...car })
    );
  } else {
    dispatch(authFailCheck(response));
    dispatch(
      notifyError(
        `${car.code} o'zgartirishda xatolik ro'y berdi: ${response.data.errors[0]}`
      )
    );
    dispatch(fetchFail(response.data));
  }
};

export const createCar = (car) => async (dispatch) => {
  dispatch(fetchRequest());

  const response = await CarService.create(car);

  if (response.ok) {
    const { id } = response.data;
    dispatch(createCarSuccess({ id, ...car }));
    dispatch(fetchCars(initialState.filter));
    dispatch(notifySuccess(`${car.number}  yaratildi.`));
  } else {
    dispatch(authFailCheck(response));
    dispatch(
      notifyError(
        `${car.code}ni yaratishda xatolik ro'y berdi.: ${response.data}`
      )
    );
    dispatch(fetchFail(response.data));
  }
};

export const deleteCar = (carId) => async (dispatch, getState) => {
  dispatch(fetchRequest());
  const car = carSelectors.selectById(getState(), carId);
  const response = await CarService.delete(carId);
  if (response.ok) {
    dispatch(deleteCarSuccess(carId));
    dispatch(notifySuccess(`${car.number} o'chirildi.`));
  } else {
    dispatch(authFailCheck(response));
    dispatch(fetchFail(response.data));
    dispatch(
      notifyError(
        `${car.code}ni o'chirishda xatolik ro'y berdi.: ${response.data}`
      )
    );
    dispatch(fetchFail(response.data));
  }
};

export const fetchContracts = () => async (dispatch) => {
  dispatch(fetchRequest());
  const response = await CarService.getContracts();
  if (response.ok) {
    dispatch(fetchContractsSuccess(response.data));
  } else {
    dispatch(authFailCheck(response));
    dispatch(fetchFail(response.data));
  }
};

export const assignCar = (contractId, oldCarId, carId, assignedDate) => async (
  dispatch
) => {
  dispatch(fetchRequest());

  const response = await CarService.assignCar(contractId, {
    carId: carId,
    assignedDate: assignedDate,
  });
  if (response.ok) {
    dispatch(assignCarSuccess({ contractId, oldCarId, carId, assignedDate }));
  } else {
    dispatch(authFailCheck(response));
    dispatch(fetchFail(response.data));
  }
};

export const unassignCar = (contractId, carId) => async (dispatch) => {
  dispatch(fetchRequest());

  const response = await CarService.unassignCar(contractId, carId);
  if (response.ok) {
    dispatch(unassignCarSuccess({ contractId, carId }));
  } else {
    dispatch(authFailCheck(response));
    dispatch(fetchFail(response.data));
  }
};

export const createDrivingNote = (contractId, data) => async (dispatch) => {
  dispatch(fetchRequest());

  const response = await CarService.createDrivingNote(contractId, data);

  if (response.ok) {
    dispatch(createDrivingNoteSuccess(response.data));
  } else {
    dispatch(authFailCheck(response));
    dispatch(fetchFail(response.data));
  }
};
